import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { RootState, MyThunkDispatch } from 'store';

import { createInviteOrUpdateUser, fetchUsersAndInvites } from 'actions/adminUser';
import { fetchGroupList } from 'actions/group';
import { fetchNationalityCountryList, fetchRestCountryList } from 'actions/i18n';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser } from 'models/adminUser';
import UsersForm from 'screens/components/UsersForm';
import * as userService from 'services/userService';
import { extractErrorMessage } from 'utils/generic';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';

const UsersFormContainer = () => {
  const dispatch: MyThunkDispatch = useDispatch();
  const { userId } = useParams<{ userId?: string }>();
  const [isUserLoading, setUserLoading] = useState<boolean>(true);
  const [userData, setUserData] = useState<AdminUser | undefined>();
  const toast = useToast();
  const { t } = useStoreTranslation();

  const {
    adminUser: { adminUserList },
    group: { groupList },
    user: { accountResult },
    i18n: { country, loadingNationalityCountries, nationalityCountries, restCountries },
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    if (!restCountries || restCountries.length === 0) {
      dispatch(fetchRestCountryList());
    }
  }, [dispatch, restCountries]);

  useEffect(() => {
    if (!userId) {
      setUserLoading(false);
      return;
    }

    const handleError = (error) =>
      toast.add({ type: 'danger', message: extractErrorMessage(error) || t('defaultError.default') });

    const loadUser = async () => {
      try {
        setUserData({ ...(await userService.fetchUser(userId)), isInvite: false });
        setUserLoading(false);
      } catch (e) {
        if (e.response.status === 404) {
          loadInvite();
        } else {
          handleError(e);
        }
      }
    };

    const loadInvite = async () => {
      try {
        setUserData({ ...(await userService.fetchInvite(userId)), isInvite: true });
      } catch (e) {
        handleError(e);
      }
      setUserLoading(false);
    };

    loadUser();
  }, [t, toast, userId]);

  const companyId = accountResult ? accountResult.companyId : '';
  const id = accountResult ? accountResult.id : '';

  const data = {
    id,
    userId,
    userData,
    isUserLoading,
    groupList,
    userList: adminUserList,
    companyId,
    country: country.culture,
    nationalityCountries,
    loadingNationalityCountries,
    restCountries,
  };

  const fetchCountries = useCallback(() => dispatch(fetchNationalityCountryList()), [dispatch]);
  const fetchGroups = useCallback(() => dispatch(fetchGroupList()), [dispatch]);
  const fetchUsers = useCallback(() => dispatch(fetchUsersAndInvites()), [dispatch]);
  const companyConfiguration = useCompanyConfiguration();

  return (
    <UsersForm
      {...data}
      fetchCountryList={fetchCountries}
      hideWhatsapp={companyConfiguration?.menuView?.hideWhatsapp}
      fetchGroupList={fetchGroups}
      fetchUserList={fetchUsers}
      saveUser={(user: AdminUser, compId: string, usId: string) =>
        dispatch(createInviteOrUpdateUser(user, compId, usId))
      }
      restCountries={restCountries}
    />
  );
};

export default UsersFormContainer;
